import {
  Address,
  ColumnDTO,
  GetItemsProps,
  exportFile,
  getExportConfigDTO,
  initialStatePhoneDTO,
  mobilePhoneToString,
  toISOString,
  validMobileForInputPhone,
} from 'medere-ui-framework'
import { axiosInstance } from '../App'
import { ComboDataDTO } from '../dto/ComboDataDTO'
import { StudiesInformListItemDTO, StudiesInformDetailsDTO, studiesInformDetailsDTOFromResponse } from '../dto/StudiesInformListItemDTO'
import { EStudyInformStatus } from '../views/StudiesInform/enums'
import { FileDTO } from '../interface/interface'

export interface StudiesInformListQueryParams {
  studyInformStatus?: EStudyInformStatus | EStudyInformStatus[]
  typeFilter?: string | null
  multiSortMeta?: unknown
  filterDTO?: unknown
  cardFilterDTO?: unknown
}


export function getAllStudiesInform(
  offSet: number,
  pageSize: number,
  params: StudiesInformListQueryParams = {},
): Promise<StudiesInformListItemDTO[]> {
  const studiesInformStatus = Array.isArray(params.studyInformStatus)
    ? params.studyInformStatus
    : params.studyInformStatus !== undefined ? [params.studyInformStatus] : []
  const typeFilter = params.typeFilter ? Number(params.typeFilter) : null
  const filters = params.filterDTO ?? params.cardFilterDTO ?? null

  return axiosInstance.post('/api/secured/studiesinform/getAllStudiesInform', {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters,
    },
    studiesInformStatus,
    typeFilter,
  }).then((response) => response.data as StudiesInformListItemDTO[])
}

export function exportStudiesInform(
  params: StudiesInformListQueryParams,
  columns: ColumnDTO[],
) {
  const studiesInformStatus = Array.isArray(params.studyInformStatus)
    ? params.studyInformStatus
    : params.studyInformStatus !== undefined ? [params.studyInformStatus] : []
  const typeFilter = params.typeFilter ? Number(params.typeFilter) : null
  const filterDTO = params.filterDTO ?? params.cardFilterDTO ?? null

  return exportFile(
    '/api/secured/studiesinform/exportStudiesInform',
    {
      ...getExportConfigDTO({ filterDTO, multiSortMeta: params.multiSortMeta }, columns),
      studiesInformStatus,
      typeFilter,
    },
  )
}

export function getStudyById(studyInformId: number): Promise<StudiesInformDetailsDTO> {
  return axiosInstance.post('/api/secured/studiesinform/getStudyById', {
    params: {
      studyInformId,
    },
  }).then((response) => studiesInformDetailsDTOFromResponse(response.data))
}

export function getStudyTypes(): Promise<Array<{ label: string; value: string }>> {
  return axiosInstance.post('/api/secured/studiesinform/getStudyTypes', {
    params: {},
  }).then((response) => (response.data as Array<{ label: string; value: string }>))
}

export interface SaveStudyInformDeterminationPayload {
  determinationId: number
  value: string
}

export interface SaveStudyInformPayload {
  studyOrderId: number
  description: string
  result: string
  markAsInformed: boolean
  determinations: SaveStudyInformDeterminationPayload[]
}

export function saveStudyInform(payload: SaveStudyInformPayload): Promise<void> {
  return axiosInstance.post('/api/secured/studiesinform/saveStudyInform', payload)
    .then(() => undefined)
}

export interface CreateManualStudyInformPayload {
  patientId: number
  requestingDoctorId: number
  studyTypeId: number
  description: string
  result: string
  markAsInformed: boolean
  determinations: SaveStudyInformDeterminationPayload[]
}

/** Crea la orden de estudio y su informe en un solo paso. Devuelve el id de la orden creada. */
export function createManualStudyInform(payload: CreateManualStudyInformPayload): Promise<number> {
  return axiosInstance.post('/api/secured/studiesinform/createManualStudyInform', payload)
    .then((response) => response.data as number)
}

export interface StudyInformImageDTO {
  id: number
  fileName: string
  description: string | null
}

export function getImagesByStudyOrder(studyOrderId: number): Promise<StudyInformImageDTO[]> {
  return axiosInstance.post('/api/secured/studiesinform/getImagesByStudyOrder', {
    params: { studyOrderId },
  }).then((response) => response.data as StudyInformImageDTO[])
}

export function getStudyImageUrl(id: number): string {
  const base = axiosInstance.defaults.baseURL ?? ''
  return `${base}/api/secured/studiesinform/getImageFile?id=${id}`
}

export function uploadStudyInformImage(file: File, studyOrderId: number): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('studyOrderId', String(studyOrderId))
  return axiosInstance.post('/api/secured/studiesinform/uploadImageFile', formData)
    .then((response) => response.data as string)
}

export function cancelStudyInform(studyDiagnosticId: number, reason: string): Promise<void> {
  return axiosInstance.post('/api/secured/studiesinform/cancelStudyInform', {
    studyOrderId: studyDiagnosticId,
    reason,
  }).then(() => undefined)
}

export function revertStudyInformToPending(studyDiagnosticId: number): Promise<void> {
  return axiosInstance.post('/api/secured/studiesinform/revertStudyInformToPending', {
    studyOrderId: studyDiagnosticId,
  }).then(() => undefined)
}

export function validateStudyInform(studyDiagnosticId: number): Promise<void> {
  return axiosInstance.post('/api/secured/studiesinform/validateStudyInform', {
    studyOrderId: studyDiagnosticId,
  }).then(() => undefined)
}

export function generateStudyInformPdf(id: number): Promise<void> {
  return axiosInstance
    .post(`/api/secured/studiesinform/${id}/generatePdf`)
    .then((response) => {
      const base64: string = response.data?.encodedPdf
      if (!base64) return
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      window.open(url, '_blank')
    })
}

export function getStudyInformPdf(id: number): Promise<string> {
  return axiosInstance
    .post(`/api/secured/studiesinform/${id}/generatePdf`)
    .then((response) => {
      const base64: string = response.data?.encodedPdf
      if (!base64) throw new Error('No PDF data received')
      return base64
    })
}

export async function getStudyInformPdfFile(id: number): Promise<FileDTO> {
  const response = await axiosInstance.post(`/api/secured/studiesinform/${id}/generatePdf`)
  const file = response.data as FileDTO
  if (!file?.encodedPdf) throw new Error('No PDF data received')
  return file
}

export interface ScheduleMessageResponseDTO {
  success: boolean
  message: string
  statusCode: number
  taskmessageid: number | null
}

export function sendStudyInformByMessageSender(
  studyOrderId: number,
  messageTypeId: number,
  mobileNumber?: string | null,
  email?: string | null,
): Promise<ScheduleMessageResponseDTO> {
  return axiosInstance
    .post(`/api/secured/studiesinform/${studyOrderId}/sendByMessageSender`, { messageTypeId, mobileNumber, email })
    .then((response) => response.data as ScheduleMessageResponseDTO)
}

